// Connexion. Deux portes, aucune inscription libre (D-014) :
//  - adultes : e-mail → code à 6 chiffres reçu par e-mail (compte existant) ;
//  - mineures sans e-mail : identifiant + code perso (D-010, D-015), vérifiés
//    par la fonction serveur `code-login`.
import { isValidCode, isValidHandle, normalizeHandle } from '../../supabase/functions/_shared/codes';
import { supabase, supabaseAnonKey, supabaseUrl } from './supabase';
import { disablePush } from './notifications';

export type LoginResult = { ok: true } | { ok: false; message: string };

const client = () => {
  if (!supabase) throw new Error('Base non configurée');
  return supabase;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Envoie le code par e-mail. Même réponse que l'adresse soit connue ou non. */
export async function sendEmailCode(email: string): Promise<LoginResult> {
  const e = email.trim().toLowerCase();
  if (!EMAIL_RE.test(e)) return { ok: false, message: 'Adresse e-mail invalide.' };
  const { error } = await client().auth.signInWithOtp({ email: e, options: { shouldCreateUser: false } });
  if (error && (error.status === 429 || /rate/i.test(error.message))) {
    return { ok: false, message: 'Trop de demandes. Réessaie dans quelques minutes.' };
  }
  return { ok: true };   // adresse inconnue : on ne le dit pas
}

export async function verifyEmailCode(email: string, token: string): Promise<LoginResult> {
  const t = token.replace(/\s+/g, '');
  if (!isValidCode(t)) return { ok: false, message: 'Le code fait 6 chiffres.' };
  const { error } = await client().auth.verifyOtp({ email: email.trim().toLowerCase(), token: t, type: 'email' });
  return error ? { ok: false, message: 'Code incorrect ou expiré.' } : { ok: true };
}

const hhmm = (iso: string) => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

export async function signInWithCode(handleInput: string, codeInput: string): Promise<LoginResult> {
  const handle = normalizeHandle(handleInput);
  const code = codeInput.replace(/\s+/g, '');
  if (!isValidHandle(handle) || !isValidCode(code)) return { ok: false, message: 'Identifiant ou code incorrect.' };
  let r: Response;
  try {
    r = await fetch(`${supabaseUrl}/functions/v1/code-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', apikey: supabaseAnonKey!, Authorization: `Bearer ${supabaseAnonKey}` },
      body: JSON.stringify({ handle, code }),
    });
  } catch {
    return { ok: false, message: 'Pas de connexion au serveur.' };
  }
  const body = await r.json().catch(() => ({}));
  if (r.status === 429) {
    return { ok: false, message: `Trop d'essais. Réessaie après ${hhmm(body.until)}, ou demande un nouveau code à ton coach ou à tes parents.` };
  }
  if (!r.ok || !body.access_token) return { ok: false, message: 'Identifiant ou code incorrect.' };
  const { error } = await client().auth.setSession({ access_token: body.access_token, refresh_token: body.refresh_token });
  return error ? { ok: false, message: 'Connexion impossible, réessaie.' } : { ok: true };
}

/** Instance de TEST (build VITE_DEMO=1) : comptes de démo en un clic, sans e-mail. */
export const demoMode = import.meta.env.VITE_DEMO === '1';
export type DemoWho = 'admin' | 'coach' | 'sarah' | 'parent' | 'kenza' | 'partner' | 'karim' | 'ines' | 'technique' | 'lucie' | 'hugo';

export async function demoSignIn(who: DemoWho): Promise<LoginResult> {
  let r: Response;
  try {
    r = await fetch(`${supabaseUrl}/functions/v1/demo-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', apikey: supabaseAnonKey!, Authorization: `Bearer ${supabaseAnonKey}` },
      body: JSON.stringify({ who }),
    });
  } catch {
    return { ok: false, message: 'Pas de connexion au serveur.' };
  }
  const body = await r.json().catch(() => ({}));
  // Partenaire : son espace est un lien privé, sans compte (D-033).
  if (r.ok && typeof body.partner_path === 'string' && /^\/p\/[0-9a-f]{32,128}$/.test(body.partner_path)) {
    location.assign(body.partner_path);
    return { ok: true };
  }
  if (!r.ok || !body.access_token) return { ok: false, message: 'Club de démo absent de ce serveur.' };
  const { error } = await client().auth.setSession({ access_token: body.access_token, refresh_token: body.refresh_token });
  return error ? { ok: false, message: 'Connexion impossible, réessaie.' } : { ok: true };
}

export async function signOut(): Promise<void> {
  // Un téléphone partagé ne doit plus recevoir les notifications de ce compte.
  await disablePush().catch(() => undefined);
  await client().auth.signOut();
}
